import { useLayoutEffect, useState } from 'react';
import api from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://backend.rainbitx.com';
const configuredBackend = new URL(API_BASE_URL, window.location.origin);
const blobCache = new Map();
const inFlightRequests = new Map();

const isPrivateMedia = (src) => typeof src === 'string' && src.includes('/api/files/private/');

const resolvePrivateMedia = (src) => {
  if (!isPrivateMedia(src)) return null;
  try {
    const resolved = new URL(src, configuredBackend);
    return resolved.origin === configuredBackend.origin && resolved.pathname.startsWith('/api/files/private/')
      ? resolved.href
      : null;
  } catch {
    return null;
  }
};

const fetchPrivateBlob = (url) => {
  if (blobCache.has(url)) return Promise.resolve(blobCache.get(url));
  if (inFlightRequests.has(url)) return inFlightRequests.get(url);

  const request = api.get(url, { responseType: 'blob', withCredentials: true })
    .then((response) => {
      blobCache.set(url, response.data);
      return response.data;
    })
    .finally(() => inFlightRequests.delete(url));
  inFlightRequests.set(url, request);
  return request;
};

const LoadingMedia = () => (
  <span className='d-inline-flex align-items-center gap-2 text-muted small' role='status'>
    <span className='spinner-border spinner-border-sm' aria-hidden='true' />
    Loading media...
  </span>
);

const AuthenticatedMedia = ({ src, as = 'img', openOnClick = false, showOpenButton = false, onLoadError, ...props }) => {
  const privateMedia = isPrivateMedia(src);
  const [state, setState] = useState(privateMedia ? 'loading' : 'ready');
  const [resolvedSrc, setResolvedSrc] = useState(privateMedia ? null : src);

  useLayoutEffect(() => {
    let objectUrl;
    let cancelled = false;

    if (!isPrivateMedia(src)) {
      setResolvedSrc(src);
      setState('ready');
      return undefined;
    }

    const privateUrl = resolvePrivateMedia(src);
    setResolvedSrc(null);
    setState('loading');
    if (!privateUrl) {
      setState('failed');
      onLoadError?.();
      return undefined;
    }

    const cachedBlob = blobCache.get(privateUrl);
    if (cachedBlob) {
      objectUrl = URL.createObjectURL(cachedBlob);
      setResolvedSrc(objectUrl);
      setState('ready');
      return () => URL.revokeObjectURL(objectUrl);
    }

    fetchPrivateBlob(privateUrl)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setState('failed');
          onLoadError?.();
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, onLoadError]);

  const openMedia = async () => {
    if (!src) return;
    if (!isPrivateMedia(src)) {
      window.open(src, '_blank', 'noopener,noreferrer');
      return;
    }
    const privateUrl = resolvePrivateMedia(src);
    if (!privateUrl) return;
    try {
      const blob = await fetchPrivateBlob(privateUrl);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch {
      setState('failed');
      onLoadError?.();
    }
  };

  if (state === 'loading') return <LoadingMedia />;
  if (state === 'failed') return <span className='text-muted small'>Preview unavailable — original file retained</span>;

  const Component = as;
  return (
    <>
      <Component src={resolvedSrc} {...props} onClick={openOnClick ? openMedia : props.onClick} />
      {showOpenButton && <button type='button' className='btn btn-link btn-sm p-0 mt-1' onClick={openMedia}>Open media in new tab</button>}
    </>
  );
};

export default AuthenticatedMedia;
